import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID } from '@angular/core';
import { CompanyController, NotificationEntity } from '@domain';
import { TranslateService } from '@ngx-translate/core';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';

interface NotificationVisual {
    icon: string;
    color: string;
    typeClass: string;
}

@Component({
    selector: 'notification-card',
    templateUrl: 'notification-card.component.html',
    styleUrl: 'notification-card.component.scss',
    imports: [CommonModule, RaIconComponent],
})
export class NotificationCardComponent {
    public notification = input.required<NotificationEntity>();

    /** Exibe o indicador de status não lido (apenas na aba de não lidas). */
    public showPin = input<boolean>(true);

    private _translate = inject(TranslateService);
    // Antes, as datas usavam `formatDate(..., 'dd/MM/yyyy', 'en-US')`: formato BRASILEIRO com
    // locale AMERICANO — o pior dos dois mundos. Agora sai do LOCALE_ID real do usuário.
    private _locale = inject(LOCALE_ID);
    private _companyController = inject(CompanyController);

    /** Chip: ícone pela categoria (`notificationId`), cor pela severidade (`type`). */
    public visual = computed<NotificationVisual>(() => {
        const icon = this._iconFor(this.notification().notificationId);
        switch (String(this.notification().type ?? '').toLowerCase()) {
            case 'success':
                return { icon, color: 'accentGreen', typeClass: 'type-success' };
            case 'warning':
                return { icon, color: 'accentOrange', typeClass: 'type-warning' };
            case 'error':
                return { icon, color: 'accentRed', typeClass: 'type-error' };
            default:
                return { icon, color: 'primary', typeClass: 'type-info' };
        }
    });

    /** Mostra o selo da empresa apenas quando vem de outra empresa. */
    public showCompany = computed<boolean>(() => {
        const companyId = this.notification().company?._id;
        return !!companyId && companyId !== this._companyController.currentCompany()?._id;
    });

    private _iconFor(notificationId: string | null | undefined): string {
        const id = String(notificationId ?? '').toUpperCase();

        if (id === 'DOCUMENT_SIGNED_CONTRACT') return 'pen-draw';
        if (id.includes('DOCUMENT')) return 'file';
        if (id.startsWith('ORDER')) return 'folder';
        if (id.startsWith('COMPANY_INVITE')) return 'email';
        if (id.includes('PAYMENT') || id === 'SUBSCRIPTION_REFUNDED') return 'dollar-circle';
        if (id.startsWith('SUBSCRIPTION')) return 'crown';
        if (id === 'USER_ADDED_AS_ADMIN' || id === 'USER_REMOVED_AS_ADMIN') return 'user-tick';
        if (id.startsWith('USER')) return 'profile';

        return 'notification-bell';
    }

    /** Data formatada como tempo relativo (data completa fica no tooltip). */
    public relativeTime = computed<string>(() => {
        const created = this.notification().createdAt;
        if (!created) return '';

        const date = new Date(created);
        const diffMs = Date.now() - date.getTime();
        const minutes = Math.floor(diffMs / 60000);

        if (diffMs < 60000) return this._translate.instant('notificationList.timeNow');
        if (minutes < 60)
            return this._translate.instant('notificationList.timeMinutesAgo', { count: minutes });

        const hours = Math.floor(minutes / 60);
        if (hours < 24)
            return this._translate.instant('notificationList.timeHoursAgo', { count: hours });

        const days = Math.floor(hours / 24);
        if (days === 1) return this._translate.instant('notificationList.timeYesterday');
        if (days < 7)
            return this._translate.instant('notificationList.timeDaysAgo', { count: days });
        if (days < 28)
            return this._translate.instant('notificationList.timeWeeksAgo', {
                count: Math.floor(days / 7),
            });

        return formatDate(date, 'shortDate', this._locale);
    });

    /** Data/hora completa para o tooltip. */
    public fullDate = computed<string>(() => {
        const created = this.notification().createdAt;
        if (!created) return '';

        return formatDate(new Date(created), 'short', this._locale);
    });
}
